import { useFormContext } from "react-hook-form";
import { SelectControllerRow } from "./SelectControllerRow";

import { FORM_FIELDS } from "@constants/form";
import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";
import { getConvertedMetaData } from "@helpers/inventory/metaDataConvert";
import { useCommitteeList } from "@hooks/procurements/committee/useCommitteeList";
import { Table, Text } from "@pentabd/ui";
import { ProfileInfoCard } from "./ProfileInfoCard";
import { useCommitteeById } from "@hooks/procurements/committee/useCommitteeById";
import useActionCreator from "@redux/hooks/useActionCreator";

const COMMITTEE_DETAILS = FORM_FIELDS.PROCUREMENT_DETAILS.COMMITTEE_DETAILS;

interface SelectCommitteeProps {
  name: string;
  required?: boolean;
}

export function SelectCommittee({
  name,
  required,
}: SelectCommitteeProps) {
  const [committee, setCommittee] = useState<any>([]);
  const { t } = useTranslation();
  const { committeeState } = useCommitteeList();
  const { committeeByIdFetchActionHandler } = useActionCreator();
  const {
    setValue,
    control,
    register,
    formState: { errors },
    watch,
  } = useFormContext();

  useEffect(() => {
    setCommittee(committeeState.getData?.data);
  }, [committeeState.getData]);

  const committeeWatch = watch(name);

  useEffect(() => {
    if (committeeWatch) {
      committeeByIdFetchActionHandler(committeeWatch as number);
    }
  }, [committeeWatch, watch]);

  return (
    <div className="pt-10">
      <SelectControllerRow
        control={control}
        name={name}
        label="PROCUREMENT_CREATE.COMMITTEE_DETAILS.COMMITTEE_NAME"
        options={getConvertedMetaData(committee || [])}
        register={
          required
            ? {
                ...register(name, {
                  required: "Committee Name is Required",
                }),
              }
            : { ...register(name) }
        }
        setValue={setValue}
        required={required}
        error={errors}
        selectAction={(id) => {
          committeeByIdFetchActionHandler(id as number);
        }}
      />

      {committeeState?.getSingleData && (
        <div className="d-flex flex-column gap-6 py-6">
          <div className="row">
            <div className="col-lg-3">
              <Text weight="semibold" color="title" size="sm">
                {t("PROCUREMENT_CREATE.COMMITTEE_DETAILS.COMMITTEE_PRESIDENT")}
              </Text>
            </div>
            <div className="col-lg-7">
              <ProfileInfoCard
                employee={
                  committeeState?.getSingleData?.committeeMembers.find(
                    (el) => el.memberType === "CHAIRPERSON",
                  )?.employee || ({} as any)
                }
              />
            </div>
          </div>

          <div className="row">
            <div className="col-lg-3">
              <Text weight="semibold" color="title" size="sm">
                {t("PROCUREMENT_CREATE.COMMITTEE_DETAILS.MEMBER_SECRETARY")}
              </Text>
            </div>
            <div className="col-lg-7">
              <ProfileInfoCard
                employee={
                  committeeState?.getSingleData?.committeeMembers.find(
                    (el) => el.memberType === "SECRETARY",
                  )?.employee || ({} as any)
                }
              />
            </div>
          </div>

          <div className="row">
            <div className="col-lg-3">
              <Text weight="semibold" color="title" size="sm">
                {t("PROCUREMENT_CREATE.COMMITTEE_DETAILS.COMMITTEE_MEMBERS")}
              </Text>
            </div>
            <div className="col-lg-7">
              <Table
                columns={[
                  {
                    id: 1,
                    name: t("PROCUREMENT_CREATE.COMMITTEE_DETAILS.MEMBER_NAME"),
                    key: "employee",
                    render: (data: any, row: any) => (
                      <ProfileInfoCard employee={row?.employee} />
                    ),
                  },
                ]}
                rows={committeeState?.getSingleData?.committeeMembers || []}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
